/**
 * One id system for sources, shared by the mock stores and the API.
 *
 * The problem this replaces: `CollaboratorSourceLink.sourceId` was a single
 * opaque string (`acc_1`, `out_3`) that had to encode *both* which kind of
 * source it was and which one. The API splits the same fact across three columns
 * — `source_kind` plus `said` or `moid` — so every read and write across the
 * boundary needed parsing, and a string that failed to parse produced a link
 * pointing at nothing with no error.
 *
 * Sources now carry numeric ids matching their primary keys (`said`, `moid`),
 * and the kind is explicit rather than encoded in a prefix. Where a single
 * string is genuinely needed — a React key, a Set membership test, a URL
 * fragment — `sourceKey` produces a stable `"account:12"` and `parseSourceKey`
 * reverses it.
 *
 * Chosen over keeping a translation layer at the API boundary: two id systems
 * means every new call site is a chance to forget the conversion, and the bug
 * that produces — a link silently attached to the wrong source — is invisible
 * until someone emails the wrong person.
 */

export type SourceKind = 'account' | 'outlet';

/** Points at exactly one account or outlet. Mirrors the API's (kind, id) pair. */
export interface SourceRef {
  sourceKind: SourceKind;
  /** `said` for an account, `moid` for an outlet. */
  sourceId: number;
}

/** Stable single-string form, for React keys and Set membership. */
export function sourceKey(ref: SourceRef | null | undefined): string {
  if (!ref) return '';
  return `${ref.sourceKind}:${ref.sourceId}`;
}

/** Reverse of `sourceKey`. Returns null rather than a half-built ref. */
export function parseSourceKey(key: string): SourceRef | null {
  const [kind, raw] = (key || '').split(':');
  if (kind !== 'account' && kind !== 'outlet') return null;
  const sourceId = Number(raw);
  return Number.isFinite(sourceId) ? { sourceKind: kind, sourceId } : null;
}

export function sameSource(a: SourceRef | null | undefined,
                           b: SourceRef | null | undefined): boolean {
  if (!a || !b) return false;
  return a.sourceKind === b.sourceKind && a.sourceId === b.sourceId;
}

/**
 * Build a ref from the API's shape, where the id lives on whichever of `said`
 * or `moid` is populated.
 */
export function refFromApi(row: {
  source_kind?: string | null;
  said?: number | null;
  moid?: number | null;
}): SourceRef | null {
  if (row.said != null) return { sourceKind: 'account', sourceId: row.said };
  if (row.moid != null) return { sourceKind: 'outlet', sourceId: row.moid };
  return null;
}

/** Split a ref back onto the API's columns for a write. */
export function refToApi(ref: SourceRef): {
  source_kind: SourceKind;
  source_id: number;
} {
  return { source_kind: ref.sourceKind, source_id: ref.sourceId };
}
